import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaTimes, FaVideo } from "react-icons/fa";
import ProductSearchSelect from "./ProductSearchSelect";
import PlaybackVideo from "./PlaybackVideo";
import "../../Banner/components/AddBannerModal.css";
import "./AddVideoModal.css";

const VIDEO_ACCEPT =
  ".mp4,.mov,.avi,.wmv,.flv,.mpeg,.mpg,.m4v,.3gp,.3g2,.mj2,.webm,video/*";

/**
 * Upload / current clip first, then product dropdown (Add + Edit).
 * Saved clip uses PlaybackVideo (fixes /api/storage URLs vs public storage).
 */
const AddVideoModal = ({
  showModal,
  onClose,
  onSave,
  saveLoading,
  isEditMode,
  selectedVideo,
}) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [existingDismissed, setExistingDismissed] = useState(false);
  const fileInputRef = useRef(null);
  const uploadInputId =
    isEditMode && selectedVideo?._id
      ? `video-upload-${selectedVideo._id}`
      : "video-upload-new";

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl]);

  useEffect(() => {
    if (!showModal) return;
    setVideoFile(null);
    setVideoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setExistingDismissed(false);
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (isEditMode && selectedVideo) {
      setSelectedProduct(
        selectedVideo.productId
          ? {
              _id: selectedVideo.productId,
              name: selectedVideo.productName || "",
            }
          : null
      );
    } else {
      setSelectedProduct(null);
    }
  }, [showModal, isEditMode, selectedVideo]);

   useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);
  
  const savedRaw = selectedVideo?.videoUrl?.trim() ?? "";

  const showExistingSaved = useMemo(
    () =>
      Boolean(
        showModal &&
          isEditMode &&
          savedRaw &&
          !videoFile &&
          !existingDismissed
      ),
    [showModal, isEditMode, savedRaw, videoFile, existingDismissed]
  );

  if (!showModal) return null;

  const showPickNewZone =
    !isEditMode || existingDismissed || Boolean(videoFile);

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setVideoFile(file);
    setVideoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const handleRemoveNewFile = () => {
    setVideoFile(null);
    setVideoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (isEditMode && savedRaw) {
      setExistingDismissed(false);
    }
  };

  const handleDismissExisting = () => {
    setExistingDismissed(true);
  };

  const handleSubmit = () => {
    onSave({
      videoFile,
      productId: selectedProduct?._id || "",
      productName: selectedProduct?.name || "",
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content add-edit-modal"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="modal-header">
          <h3>{isEditMode ? "Edit Video" : "Add Video"}</h3>
          <button
            type="button"
            className="modal-close video-modal-close-right"
            onClick={onClose}
            aria-label="Close"
            disabled={saveLoading}
          >
            <FaTimes aria-hidden />
          </button>
        </div>

        <div className="profile-form">
          {showExistingSaved && (
            <div className="video-modal-existing-shell">
              <div
                className="video-modal-existing-wrap"
                onClick={handleDismissExisting}
                style={{ cursor: "pointer" }}
                title="Click to change video"
              >
                <PlaybackVideo
                  rawUrl={savedRaw}
                  className="video-modal-existing"
                  label="Current uploaded video"
                  fallbackClassName="video-modal-existing-fallback"
                />
                <button
                  type="button"
                  className="video-modal-existing-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismissExisting();
                  }}
                  disabled={saveLoading}
                  aria-label="Clear current video and upload new"
                  title="Remove and upload new video"
                >
                  <FaTimes />
                </button>
              </div>
              <p className="video-modal-hint video-modal-hint--tight">
                Tap × or the video to remove this clip, then choose a new file below.
              </p>
            </div>
          )}

          {showPickNewZone && (
            <div className="form-group">
              <input
                ref={fileInputRef}
                id={uploadInputId}
                type="file"
                accept={VIDEO_ACCEPT}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div
                className="upload-area video-modal-upload"
                onClick={(e) => {
                  if (videoPreviewUrl) return;
                  handlePickFile();
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (videoPreviewUrl) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handlePickFile();
                  }
                }}
              >
                {!videoPreviewUrl ? (
                  <div className="upload-placeholder">
                    <FaVideo />
                    <p>
                      {isEditMode
                        ? "Choose new video file"
                        : "Choose video file"}
                    </p>
                    <span>
                      mp4, mov, avi, wmv, flv, mpeg, mpg, m4v, 3gp, 3g2, mj2,
                      webm
                    </span>
                    <button
                      type="button"
                      className="video-modal-browse-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePickFile();
                      }}
                      disabled={saveLoading}
                    >
                      Choose file
                    </button>
                  </div>
                ) : (
                  <div className="video-modal-preview-wrap">
                    <div className="video-modal-preview-inner">
                      <div className="video-modal-preview-video-shell">
                        <video
                          src={videoPreviewUrl}
                          className="video-modal-preview"
                          controls
                          muted
                          playsInline
                        />
                        <button
                          type="button"
                          className="video-modal-preview-dismiss"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveNewFile();
                          }}
                          disabled={saveLoading}
                          aria-label="Remove file"
                          title="Remove file"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                    <div className="video-modal-preview-toolbar">
                      <p className="video-modal-filename">{videoFile?.name}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <ProductSearchSelect
            optional
            selectedProduct={selectedProduct}
            onSelect={setSelectedProduct}
            disabled={saveLoading}
          />

          <div className="form-actions">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saveLoading}
              aria-busy={saveLoading}
            >
              {saveLoading
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVideoModal;
